import React from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import type { singlePersonKnowledgeCardRelayQuery } from './__generated__/singlePersonKnowledgeCardRelayQuery.graphql';
import { SinglePersonKnowledgeCardRelay } from './single-person-knowledge-card-relay';

export type SinglePersonKnowledgeCardProps = {
	accountId: string;
	cloudId: string;
	orgId: string;
	requestingUserId: string;
	sourceProduct?: string;
	onLinkClick?: (
		url: string,
		index: number,
		opts?: { isAuxClick?: boolean; isContextMenu?: boolean },
	) => void;
};

function buildSiteAri(cloudId: string, sourceProduct?: string): string {
	if (sourceProduct) {
		return `ari:cloud:${sourceProduct}::site/${cloudId}`;
	}
	return cloudId;
}

/**
 * Person knowledge card for a single selected user: loads profile, recent work, collaborators,
 * and reporting lines via one Relay query and the local `SinglePersonKnowledgeCardRelay` (`single-person-knowledge-card-relay`).
 * Must render under a Relay environment and a React `Suspense` boundary (see `PersonKnowledgeCard`).
 */
export function SinglePersonKnowledgeCard(
	props: SinglePersonKnowledgeCardProps,
): React.JSX.Element {
	const { accountId, cloudId, orgId, requestingUserId, sourceProduct, onLinkClick } = props;
	const accountIdARI = `ari:cloud:identity::user/${accountId}`;

	const data = useLazyLoadQuery<singlePersonKnowledgeCardRelayQuery>(
		graphql`
			query singlePersonKnowledgeCardRelayQuery(
				$first: Int!
				$filter: ActivityFilter
				$recommendationsQuery: SmartsRecommendationsQuery!
				$reportingLinesAaid: String!
				$reportingLinesOrgId: String!
				$reportingLinesSiteId: String!
				$singleUserAccountId: String!
				$singleUserRequestingUserId: String!
				$singleUserTenantId: String!
			) {
				activity {
					workedOn(first: $first, filter: $filter) {
						...singlePersonKnowledgeCardRelay_workedOn
					}
				}
				smarts {
					...singlePersonKnowledgeCardRelay_smarts
						@arguments(recommendationsQuery: $recommendationsQuery)
				}
				knowledgeDiscovery {
					...singlePersonKnowledgeCardRelay_user
						@arguments(
							accountId: $singleUserAccountId
							requestingUserId: $singleUserRequestingUserId
							tenantId: $singleUserTenantId
						)
					...singlePersonKnowledgeCardRelay_reportingLines
						@arguments(
							aaid: $reportingLinesAaid
							orgId: $reportingLinesOrgId
							siteId: $reportingLinesSiteId
						)
				}
			}
		`,
		{
			first: 7,
			singleUserAccountId: accountId,
			singleUserRequestingUserId: requestingUserId,
			singleUserTenantId: cloudId,
			filter: { actors: [accountIdARI] },
			recommendationsQuery: {
				context: {
					userId: accountId,
					tenantId: cloudId,
				},
				modelRequestParams: {
					caller: 'search-ai',
					experience: 'CgUserNearbyUser',
				},
				requestingUserId,
				maxNumberOfResults: 8,
			},
			reportingLinesAaid: accountId,
			reportingLinesOrgId: orgId,
			reportingLinesSiteId: buildSiteAri(cloudId, sourceProduct),
		},
	);
	return (
		<SinglePersonKnowledgeCardRelay
			userFragment={data.knowledgeDiscovery ?? undefined}
			workedOnFragment={data.activity?.workedOn ?? undefined}
			smartsFragment={data.smarts ?? undefined}
			reportingLinesFragment={data.knowledgeDiscovery ?? undefined}
			cloudId={cloudId}
			orgId={orgId}
			siteId={buildSiteAri(cloudId, sourceProduct)}
			requestingUserId={requestingUserId}
			testId="individual-person-knowledge-card"
			onLinkClick={onLinkClick}
		/>
	);
}
